import React, { useEffect, useRef } from 'react';
import { CheckCircle, XCircle, Info } from 'lucide-react';

import styles from './SyncStatusMessage.module.scss';

/**
 * SyncStatusMessage - A reusable component for displaying sync status messages
 * 
 * This component displays status messages (success, error, info) with consistent
 * styling and auto-dismiss functionality. It uses ARIA live regions to announce
 * messages to screen readers.
 * 
 * @example
 * ```tsx
 * <SyncStatusMessage
 *   message="Games synced successfully!"
 *   type="success"
 *   visible={true}
 *   onDismiss={() => setVisible(false)}
 *   autoDismissMs={5000}
 * />
 * ```
 */

export type MessageType = 'success' | 'error' | 'info';

export interface SyncStatusMessageProps {
  /** The message to display */
  message: string;
  
  /** Type of message (determines styling and icon) */
  type: MessageType;
  
  /** Whether the message is currently visible */
  visible: boolean;
  
  /** Callback when message should be dismissed */
  onDismiss?: () => void;
  
  /** Auto-dismiss timeout in milliseconds (default: 5000, 0 to disable) */
  autoDismissMs?: number;
  
  /** Optional CSS class name for custom styling */
  className?: string;
}

const SyncStatusMessage: React.FC<SyncStatusMessageProps> = ({
  message,
  type,
  visible,
  onDismiss,
  autoDismissMs = 5000,
  className = '',
}) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Auto-dismiss functionality
  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Set up auto-dismiss if enabled and visible
    if (visible && autoDismissMs > 0 && onDismiss) {
      timeoutRef.current = setTimeout(() => {
        onDismiss();
      }, autoDismissMs);
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [visible, autoDismissMs, onDismiss]);

  // Get the appropriate icon based on message type
  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className={styles.icon} aria-hidden="true" />;
      case 'error':
        return <XCircle className={styles.icon} aria-hidden="true" />;
      case 'info':
        return <Info className={styles.icon} aria-hidden="true" />;
      default:
        return null;
    }
  };

  // Determine ARIA attributes based on message type
  const ariaRole = type === 'error' ? 'alert' : 'status';
  const ariaLive = type === 'error' ? 'assertive' : 'polite';

  // Combine CSS classes
  const messageClasses = [
    styles.statusMessage,
    styles[type],
    !visible && styles.hidden,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={messageClasses}
      role={ariaRole}
      aria-live={ariaLive}
      aria-atomic="true"
    >
      {getIcon()}
      <span>{message}</span>
    </div>
  );
};

export default SyncStatusMessage;
